import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ScrapCertificateService {
  private showScrapCertificateModal = new BehaviorSubject<boolean>(false);
  showScrapCertificateModal$ = this.showScrapCertificateModal.asObservable();

  // Store the product data for the scrap certificate modal
  private scrapProductData = new BehaviorSubject<any>(null);
  scrapProductData$ = this.scrapProductData.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private apiService: ApiService
  ) {}

  /**
   * Check if a product is a scrap part
   * @param product - The product object to check
   * @returns boolean indicating if it's a scrap part
   */
  isScrapPart(product: any): boolean {
    if (!product) return false;
    
    // Check multiple possible ways to determine if it's a scrap part
    if (product.part_type === 'Scrap Part') return true;
    if (product.part_detail?.part_type === 'Scrap Part') return true;
    
    // Check localStorage for category type
    if (isPlatformBrowser(this.platformId)) {
      const categoryType = localStorage.getItem('product_categoryType');
      if (categoryType === 'Scrap Part') return true;
    }
    
    return false;
  }

  /**
   * Handle add to cart for any product with enhanced scrap part logic
   * @param product - The product to add to cart
   * @param addToCartCallback - Callback function to execute for non-scrap parts
   * @param loginCallback - Callback function to execute when user needs to login
   */
  handleAddToCart(product: any, addToCartCallback: () => void, loginCallback?: () => void): void {
    if (this.isScrapPart(product)) {
      // Check if user is logged in first
      if (!this.isUserLoggedIn()) {
        if (loginCallback) {
          loginCallback();
        }
        return;
      }

      // Determine if certificate is required for this product
      const isCertRequired = (
        product?.part_detail?.is_certificate_required === 'True' ||
        product?.is_certificate_required === 'True'
      );

      if (isCertRequired) {
        // For scrap parts with certificate required, check category match first
        this.checkScrapCategoryAndProceed(product, addToCartCallback);
      } else {
        // Certificate not required: proceed directly without opening modal
        addToCartCallback();
      }
    } else {
      // Execute the normal add to cart logic for non-scrap parts
      addToCartCallback();
    }
  }

  /**
   * Check if user is logged in
   * @returns boolean indicating if user is logged in
   */
  private isUserLoggedIn(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    
    const userData = localStorage.getItem('userData1');
    if (!userData) {
      return false;
    }
    
    try {
      const parsedUserData = JSON.parse(userData);
      return !!(parsedUserData && parsedUserData.id);
    } catch (error) {
      return false;
    }
  }

  /**
   * Check scrap category match and proceed accordingly
   * @param product - The scrap product
   * @param addToCartCallback - Callback for direct add to cart
   */
  private checkScrapCategoryAndProceed(product: any, addToCartCallback: () => void): void {
    const payload = {
      "action_type":"",
      status:['Valid','Expiring Soon']
    };

    this.apiService.getScrapCertificateList(payload).subscribe({
      next: (response) => {
        if (response.success && response.data && response.data.scrap_certificate_list) {
          const productScrapCategory = product?.part_detail?.scrap_category || product?.scrap_category;
          const categoryMatches = response?.data?.scrap_certificate_list?.some((certificate: any) => {
            return certificate.category === productScrapCategory;
          });
          
          // Category matches - add to cart directly
          if (categoryMatches) {
            addToCartCallback();
          } else {
            this.scrapProductData.next(product);
            // Use setTimeout to ensure the modal opens immediately after the current execution cycle
            setTimeout(() => {
              this.showScrapCertificateModal.next(true);
            }, 0);
          }
        } else {
          // Category doesn't match - show certificate modal
          this.scrapProductData.next(product);
          setTimeout(() => {
            this.showScrapCertificateModal.next(true);
          }, 0);
        }
      },
      error: (error) => {
        console.error('Error checking scrap category match:', error);
        // On error, show certificate modal as fallback
        this.scrapProductData.next(product);
        setTimeout(() => {
          this.showScrapCertificateModal.next(true);
        }, 0);
      }
    });
  }

  /**
   * Open the scrap certificate modal
   * @param product - The product data to store
   */
  openScrapCertificateModal(product: any): void {
    this.scrapProductData.next(product);
    this.showScrapCertificateModal.next(true);
  }

  /**
   * Close the scrap certificate modal
   */
  closeScrapCertificateModal(): void {
    this.showScrapCertificateModal.next(false);
    this.scrapProductData.next(null);
  }

  /**
   * Get the current scrap product data
   */
  getScrapProductData(): any {
    return this.scrapProductData.value;
  }

  /**
   * Check if the scrap certificate modal is currently open
   */
  isScrapCertificateModalOpen(): boolean {
    return this.showScrapCertificateModal.value;
  }
}
